import React, { useState, useEffect } from "react";
import axios from "axios";
import { Activity, CheckCircle, AlertTriangle, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlatformHealthPage() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/health`);
      setHealth(r.data);
    } catch (e) {
      setHealth({ status: "error", error: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHealth(); }, []);

  const isHealthy = health?.status === "healthy";

  return (
    <div className="space-y-6" data-testid="platform-health-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-[#0A0A0A]">Platform Health</h2>
          <p className="text-xs text-[#888] mt-0.5">System status and service health checks</p>
        </div>
        <button onClick={fetchHealth} disabled={loading}
          className="px-3 py-2 rounded-lg border border-[#e8e0d0] text-xs font-medium text-[#555] hover:bg-[#faf7f2] transition-colors flex items-center gap-1.5 disabled:opacity-50"
          data-testid="refresh-health-btn">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className={`rounded-xl border p-5 ${isHealthy ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/50"}`}>
        <div className="flex items-center gap-3">
          {isHealthy ? (
            <CheckCircle className="h-6 w-6 text-emerald-600" />
          ) : (
            <AlertTriangle className="h-6 w-6 text-red-500" />
          )}
          <div>
            <div className={`text-sm font-bold ${isHealthy ? "text-emerald-700" : "text-red-600"}`}>
              {isHealthy ? "All Systems Operational" : "System Issues Detected"}
            </div>
            <div className="text-xs text-[#888] mt-0.5">
              {health?.version ? `Version ${health.version}` : "Checking..."}
            </div>
          </div>
        </div>
      </div>

      {health?.checks && (
        <div className="space-y-3">
          {Object.entries(health.checks).map(([key, val]) => (
            <div key={key} className="rounded-xl border border-[#e8e0d0] bg-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Activity className="h-4 w-4 text-[#888]" />
                <div>
                  <div className="text-sm font-semibold text-[#222] capitalize">{key.replace(/_/g, " ")}</div>
                  {val.engine && <div className="text-[10px] text-[#999]">Engine: {val.engine}</div>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {val.latency_ms != null && (
                  <span className="text-[10px] text-[#999]">{val.latency_ms.toFixed(1)}ms</span>
                )}
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  val.status === "healthy" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                }`}>
                  {val.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
